import React, { useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { TOURNAMENT_CREATE_PAGE, TOURNAMENT_QUICK_CREATE_FLEXIBLE_PAGE } from 'constants/pages';
import { useVairConnection } from 'hooks/useVairConnection';
import ButtonToggler from './ButtonToggler';
import { QuickCreateFormTypes, VairFlow, VairFormatOption } from '../types';

// Black VAIR wordmark already shipped in the repo.
const VAIR_LOGO_SRC = '/images/vair/vair-black.png';

interface FormatCard {
  id: VairFormatOption;
  iconSrc: string;
  /** Extra classes for the icon (e.g. recoloring a shared asset). */
  iconClassName?: string;
  title: string;
  description: string;
}

const TOURNAMENT_CARD: FormatCard = {
  id: 'tournament',
  iconSrc: '/images/player-home-v2/search-bar-icons/tournament-gradient.svg',
  title: 'Tournament',
  description: 'Round robin leading to a single elimination bracket.',
};

const TRADITIONAL_RR_CARD: FormatCard = {
  id: 'traditional-rr',
  iconSrc: '/images/programming/open-play.png',
  title: 'Traditional Round Robin',
  description: 'Each team plays all other teams in their pool.',
};

const VAIR_RR_CARD: FormatCard = {
  id: 'vair-rr',
  // Same glyph as Traditional Round Robin, rendered black (no dedicated black asset).
  iconSrc: '/images/programming/open-play.png',
  iconClassName: '[filter:brightness(0)]',
  title: 'VAIR Round Robin',
  description: 'VAIR Round Robin is for up to 9 players on 2 courts with rotating partners each round. It’s fixed.',
};

const FLEXIBLE_CARD: FormatCard = {
  id: 'flexible',
  iconSrc: '/images/player-home-v2/search-bar-icons/clinic-gradient.png',
  title: 'Flexible event',
  description: 'Collect registrations and run the event however you’d like.',
};

const FLOW_CONFIG: Record<VairFlow, { subtitle: string; cards: FormatCard[] }> = {
  'integrated-event': {
    subtitle: 'Integrated event',
    cards: [TOURNAMENT_CARD, TRADITIONAL_RR_CARD],
  },
  'rating-session': {
    subtitle: 'Rating Session',
    cards: [VAIR_RR_CARD, TRADITIONAL_RR_CARD, FLEXIBLE_CARD],
  },
};

interface Props extends QuickCreateFormTypes {
  vairFlow: VairFlow;
}

// Step shown when the user enters quick-create through a VAIR entry point
// (?vairFlow=integrated-event | rating-session). Replaces the generic
// "Is this a VAIR event?" SelectVairEvent step since the intent is already known.
export default function SelectVairFormat({ vairFlow, nextStep, prevStep, setFormData }: Props) {
  const router = useRouter();
  const { subtitle, cards } = FLOW_CONFIG[vairFlow];
  const [selected, setSelected] = useState<VairFormatOption>(cards[0].id);

  const {
    isConnected,
    loading: vairLoading,
    called: vairCalled,
    openVairConnectionModal,
    VairConnectionModal,
    VairConnectionStatusModal,
  } = useVairConnection();

  const applyFormatAndAdvance = (isVairEvent: boolean) => {
    setFormData?.((prev) => ({
      ...prev,
      whenAndWhere: { ...prev.whenAndWhere, isVairEvent, vairFormat: selected },
    }));
    nextStep?.();
  };

  const handleNext = () => {
    switch (selected) {
      case 'tournament':
        // Tournament (round robin → single elimination) uses the full tournament
        // builder, so we leave the quick-create wizard entirely.
        router.push(TOURNAMENT_CREATE_PAGE);
        return;
      case 'flexible':
        // VAIR rating session → require a connected VAIR account, then open the
        // standalone "VAIR Free format" builder.
        if (!vairLoading && !isConnected) {
          openVairConnectionModal('create');
          return;
        }
        router.push(TOURNAMENT_QUICK_CREATE_FLEXIBLE_PAGE);
        return;
      case 'vair-rr':
        // VAIR-rated format → require a connected VAIR account before proceeding
        // (mirrors the old SelectVairEvent.handleYes gate).
        if (!vairLoading && !isConnected) {
          openVairConnectionModal('create');
          return;
        }
        applyFormatAndAdvance(true);
        return;
      case 'traditional-rr':
        // Non-VAIR flow; the VAIR-required toggle in WhenAndWhere decides rating.
        applyFormatAndAdvance(false);
        return;
    }
  };

  // VAIR-rated formats need the connection status resolved before advancing.
  const isCheckingConnection =
    (selected === 'vair-rr' || selected === 'flexible') && (vairLoading || !vairCalled);

  return (
    <>
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-12 px-4 pt-10 text-center md:gap-20 md:pt-16">
        <div className="flex flex-col items-center gap-4">
          <img src={VAIR_LOGO_SRC} alt="VAIR" className="h-16 w-auto" />
          <h2 className="typography-product-display-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {subtitle}
          </h2>
        </div>

        <div className="flex w-full flex-col items-center gap-4">
          <h3 className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Select the format
          </h3>
          <div role="radiogroup" className="flex w-full flex-col gap-4 md:w-auto md:flex-row md:gap-6">
            {cards.map((card) => {
              const isSelected = selected === card.id;
              return (
                <button
                  key={card.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSelected(card.id)}
                  className={classNames(
                    'flex w-full flex-col items-center gap-2 rounded-lg px-4 py-3 text-center transition md:w-[216px]',
                    isSelected
                      ? 'bg-color-bg-lightmode-primary shadow-[0px_0px_0px_2px_#ffffff,0px_0px_0px_3px_#4e4d54,0px_4px_20px_0px_rgba(0,0,0,0.16)]'
                      : 'bg-color-bg-lightmode-secondary',
                  )}
                >
                  <img
                    src={card.iconSrc}
                    alt=""
                    className={classNames('size-8 object-contain', card.iconClassName)}
                  />
                  <span className="typography-product-card-title text-color-text-lightmode-primary">
                    {card.title}
                  </span>
                  <span className="typography-product-text-card text-color-text-lightmode-secondary">
                    {card.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <ButtonToggler
        type="button"
        prevStep={prevStep}
        nextStep={handleNext}
        nextTitle="Next"
        isLoading={false}
        disabled={isCheckingConnection}
      />

      {/* Vair connection prompt + status modals (reused from the hook) */}
      {VairConnectionModal}
      {VairConnectionStatusModal}
    </>
  );
}
